import React from "react";

import { Flex } from "../Box/Box";
import { Skeleton } from "../Skeleton/Skeleton";
import { TextVariant } from "./store/types";
import { textVariants } from "./store/variants";
import { Color } from "../Color/Color";
import { styledSafe } from "src/utils/styledComponent";

export const TextElement = styledSafe("span")<
  { variant: TextVariant } & Pick<
    TextProps,
    | "bold"
    | "textAlign"
    | "textOverflow"
    | "textTransform"
    | "textShadow"
    | "wordBreak"
    | "direction"
    | "whiteSpace"
    | "lineClamp"
    | "alignContent"
  >
>`
  display: inline;
  font-size: ${({ variant }) => textVariants[variant].fontSize};
  font-weight: ${({ bold, variant }) => (bold ? "bold" : textVariants[variant].fontWeight)};
  line-height: ${({ variant }) => textVariants[variant].lineHeight};
  color: ${({ color, variant }) => color || textVariants[variant].color || Color.textMain};
  text-align: ${({ textAlign }) => textAlign};
  text-transform: ${({ textTransform, variant }) =>
    textTransform || textVariants[variant].textTransform};
  text-overflow: ${({ textOverflow }) => textOverflow};
  text-shadow: ${({ textShadow }) => textShadow};
  overflow: ${({ textOverflow }) => textOverflow && "hidden"};
  letter-spacing: ${({ variant }) => textVariants[variant].letterSpacing};
  white-space: ${({ whiteSpace }) => whiteSpace};
  word-break: ${({ wordBreak }) => wordBreak};
  writing-mode: ${({ direction }) => (direction === "top-bottom" && "vertical-rl") || undefined};
  align-content: ${({ alignContent }) => alignContent};

  ${({ lineClamp }) =>
    lineClamp !== undefined &&
    `
      display: -webkit-box;
      -webkit-line-clamp: ${lineClamp};
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
`;

export interface TextProps {
  variant?: TextVariant;
  color?: string | false;
  isDisabled?: boolean;
  bold?: boolean;
  textAlign?: "left" | "center" | "right";
  alignContent?: "center" | "flex-start" | "flex-end" | "stretch";
  whiteSpace?: "nowrap" | "wrap";
  textOverflow?: "clip" | "ellipsis";
  textTransform?: "capitalize" | "lowercase" | "uppercase";
  textShadow?: string;
  wordBreak?: "break-all" | "break-word";
  direction?: "top-bottom";
  lineClamp?: number;
  isLoading?: boolean;
  isLoadingWidth?: string;
  className?: string;
  testId?: string;
  children: React.ReactNode;
}

export const Text = ({
  variant = "body3",
  color,
  isDisabled,
  bold,
  textAlign = "left",
  textOverflow,
  textTransform,
  textShadow,
  whiteSpace,
  wordBreak,
  direction,
  lineClamp,
  className,
  isLoading,
  isLoadingWidth = "10px",
  children,
  testId,
  alignContent,
}: TextProps) => {
  if (isLoading) {
    return (
      <Flex height={textVariants[variant].lineHeight} align="center">
        <Skeleton minWidth={isLoadingWidth} height={textVariants[variant].lineHeight} />
      </Flex>
    );
  }

  return (
    <TextElement
      className={className}
      data-variant={variant}
      variant={variant}
      color={(isDisabled && Color.textDisabled) || color || undefined}
      bold={bold}
      alignContent={alignContent}
      textAlign={textAlign}
      textOverflow={textOverflow}
      textTransform={textTransform}
      textShadow={textShadow}
      whiteSpace={whiteSpace}
      wordBreak={wordBreak}
      direction={direction}
      lineClamp={lineClamp}
      data-testid={testId}
    >
      {children}
    </TextElement>
  );
};
